import { FunctionComponent, useMemo } from 'react'
import {
  Box,
  Typography,
  Button,
  Paper,
  Divider,
  Link,
  Skeleton,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useNavigate } from 'react-router-dom'
import DashboardLayout from '../../../components/layout/layout'
import { useGetResourcesByItem } from '../../../hooks/api/user'
import { handleFileClick } from '../../../utils/fileHandler'

const ProductHighlights: FunctionComponent = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  // Fetch Product Highlights data from category 56
  const { data: resources, isLoading, error } = useGetResourcesByItem('56');

  // Organize resources by volume and issue
  const organizedResources = useMemo(() => {
    if (!resources || !Array.isArray(resources)) return { volume1: [], volume2: [], volume3: [] };

    const volume1: any[] = [];
    const volume2: any[] = [];
    const volume3: any[] = [];

    // Mapping for proper application descriptions (since API data has generic titles)
    const applicationDescriptions: { [key: string]: string } = {
      // Volume 1 mappings
      'ProductHighlightsVol1Issue1.pdf': 'E1H Pressure Switch - Steam Generators: High Pressure with Manual Reset',
      'ProductHighlightsVol1Issue2.pdf': 'E1H Pressure Switch - Medical Gas Supply: Self Regulating Safety Control',
      'ProductHighlightsVol1Issue3.pdf': 'TXR & TXL Xproof Temperature Switch - Heat Tracing: Temperature Control to Support High Load Systems',
      'ProductHighlightsVol1Issue4.pdf': 'T9692X Xproof Temperature Switch - Natural Draft Process Heaters: Exhaust Temperature Control',
      'ProductHighlightsVol1Issue5.pdf': 'MSPS Pressure Switch - Beverage Dispensers: Monitoring Supply',
      'ProductHighlightsVol1Issue6.pdf': 'B1T Pressure Switch - Hydraulic Manifolds for Hydroelectric Power: Monitoring and Controlling',
      'ProductHighlightsVol1Issue7.pdf': 'TS2000 Electronic Temperature Switch - Compactors & Balers: Monitoring and Controlling Temperature',
      'ProductHighlightsVol1Issue8.pdf': '625 Pressure Transducer - Wind Energy: Monitoring and Controlling the Brakes',
      'ProductHighlightsVol1Issue9.pdf': '425 Pressure Transducer - Air & Gas Compressors: Controlling Start/Stop',
      'ProductHighlightsVol1Issue10.pdf': 'B1X Xproof Pressure Switch - Pump Transfer Skid: Safety Shutdown',
      'Vol-1_Issue-6_Bourdon.pdf': 'B1T Pressure Switch - Hydraulic Manifolds for Hydroelectric Power: Monitoring and Controlling',
      'Vol-1_Issue-7_TS2000.pdf': 'TS2000 Electronic Temperature Switch - Compactors & Balers: Monitoring and Controlling Temperature',
      'Vol-1_Issue-8_600-Series.pdf': '625 Pressure Transducer - Wind Energy: Monitoring and Controlling the Brakes',
      'Vol-1_Issue-9_400-Series.pdf': '425 Pressure Transducer - Air & Gas Compressors: Controlling Start/Stop',

      // Volume 2 mappings
      'Vol-2_Issue-1_LevelSite.pdf': 'LevelSite® Level Control - Fuel Trailers: Using a LevelSite® for Easy Visual Indication',
      'Vol-2_Issue-2_600-Series.pdf': '625 Pressure Transducer - Mobile Hydraulics: Monitoring and Controlling Excavators',
      'Vol-2_Issue-3_DPDT.pdf': 'DPD1T Differential Pressure - Filtration Systems: Monitoring Filter Efficiency',
      'Vol-2_Issue-4_8000-Series.pdf': 'Series 8000 Pressure Switch - Brake Monitoring System: Brake Failure Notification',
      'Vol-2_Issue-5_7000-Series.pdf': 'Series 7000 Pressure Switch -Mobile Hydraulics: Braking and Transmission System',
      'ProductHighlightsVol2Issue1.pdf': 'LevelSite® Level Control - Fuel Trailers: Using a LevelSite® for Easy Visual Indication',
      'ProductHighlightsVol2Issue2.pdf': '625 Pressure Transducer - Mobile Hydraulics: Monitoring and Controlling Excavators',
      'ProductHighlightsVol2Issue3.pdf': 'DPD1T Differential Pressure - Filtration Systems: Monitoring Filter Efficiency',
      'ProductHighlightsVol2Issue4.pdf': 'Series 8000 Pressure Switch - Brake Monitoring System: Brake Failure Notification',
      'ProductHighlightsVol2Issue5.pdf': 'Series 7000 Pressure Switch -Mobile Hydraulics: Braking and Transmission System',

      // Volume 3 mappings
      'ProductHighlightsVol3Issue1.pdf': 'UDS1V2 Electronic Pressure Switch - Plastic Injection Molding: Controlling Hydraulic Ram Pressure',
      'ProductHighlightsVol3Issue2.pdf': 'Series 9000 Valve - Hyperbaric Chambers: Controlling Gas Delivery',

      // Special files that don't follow standard naming - these should be excluded from Product Highlights
      'Explosion_Proof_Pressure_Switches_Wellhead_Control_Panels.pdf': 'Explosion Proof Pressure Switches - Wellhead Control Panels',
      'MONITORING HYDROGEN GAS PRESSURE IN  SOLID OXIDE FUEL CELLS-2024 Applicatio': 'Monitoring Hydrogen Gas Pressure in Solid Oxide Fuel Cells',
      'MONITORING_HYDROGEN_GAS_PRESSURE_IN _SOLID_OXIDE_FUEL_CELLS.pdf': 'Monitoring Hydrogen Gas Pressure in Solid Oxide Fuel Cells',
    };

    // Files that should be excluded from Product Highlights (they're not actual Product Highlights)
    const excludedFiles = [
      'Explosion_Proof_Pressure_Switches_Wellhead_Control_Panels.pdf',
      'MONITORING HYDROGEN GAS PRESSURE IN  SOLID OXIDE FUEL CELLS-2024 Applicatio',
      'MONITORING_HYDROGEN_GAS_PRESSURE_IN _SOLID_OXIDE_FUEL_CELLS.pdf'
    ];

    resources.forEach((resource) => {
      const title = resource.title || '';
      const filename = resource.filename || '';
      
      // Skip excluded files
      if (excludedFiles.includes(filename)) {
        return;
      }
      
      // Extract volume and issue from filename or title
      let volume = 1;
      let issue = 1;
      
      // First try to extract from filename (more reliable)
      if (filename) {
        // Match patterns like: ProductHighlightsVol1Issue1.pdf, Vol-1_Issue-6_Bourdon.pdf
        const volMatch = filename.match(/Vol(\d+)/i) || filename.match(/Vol-(\d+)/i);
        const issueMatch = filename.match(/Issue(\d+)/i) || filename.match(/Issue-(\d+)/i);
        
        if (volMatch) volume = parseInt(volMatch[1]);
        if (issueMatch) issue = parseInt(issueMatch[1]);
      }
      
      // If filename didn't work, try title
      if (volume === 1 && issue === 1) {
        if (title.includes('Volume 1')) volume = 1;
        else if (title.includes('Volume 2')) volume = 2;
        else if (title.includes('Volume 3')) volume = 3;
        
        const issueMatch = title.match(/Issue (\d+)/i) || title.match(/Issue-(\d+)/i);
        if (issueMatch) issue = parseInt(issueMatch[1]);
      }

      // Create a clean display title
      let displayTitle = title;
      
      // Check if we have a mapping for this filename
      if (applicationDescriptions[filename]) {
        displayTitle = applicationDescriptions[filename];
      } else {
        // Remove generic "Product HighLights - Volume X, Issue Y" prefix
        displayTitle = displayTitle.replace(/Product HighLights - Volume \d+, Issue \d+\r?\n?/gi, '').trim();
        
        // If title is empty after cleaning, use filename or original title
        if (!displayTitle) {
          // Try to extract meaningful title from filename
          if (filename) {
            // Remove file extension and common prefixes
            let cleanFilename = filename.replace(/\.pdf$/i, '');
            cleanFilename = cleanFilename.replace(/ProductHighlightsVol\d+Issue\d+/i, '');
            cleanFilename = cleanFilename.replace(/Vol-\d+_Issue-\d+_/i, '');
            cleanFilename = cleanFilename.replace(/_/g, ' ');
            cleanFilename = cleanFilename.trim();
            
            if (cleanFilename) {
              displayTitle = cleanFilename;
            } else {
              displayTitle = title || filename;
            }
          } else {
            displayTitle = title;
          }
        }
      }

      const resourceItem = {
        ...resource,
        volume,
        issue,
        displayTitle
      };

      switch (volume) {
        case 1:
          volume1.push(resourceItem);
          break;
        case 2:
          volume2.push(resourceItem);
          break;
        case 3:
          volume3.push(resourceItem);
          break;
        default:
          volume1.push(resourceItem);
      }
    });

    // Sort by issue number (highest to lowest)
    volume1.sort((a, b) => b.issue - a.issue);
    volume2.sort((a, b) => b.issue - a.issue);
    volume3.sort((a, b) => b.issue - a.issue);

    // Helper function to check if a file already exists in the volumes
    const fileExists = (filename: string, volumes: any[][]) => {
      return volumes.some(volume => 
        volume.some(item => item.filename === filename)
      );
    };

    // Add missing Volume 3 data (not in API response) - using configurable base URL
    const baseUrl = window.location.origin; // Gets current domain dynamically
    const missingVolume3Data = [
      {
        itemId: 'vol3-issue2',
        filename: 'ProductHighlightsVol3Issue2.pdf',
        title: 'Series 9000 Valve - Hyperbaric Chambers: Controlling Gas Delivery',
        downloadUrl: `${baseUrl}/api/v1/resources/download/56/ProductHighlightsVol3Issue2.pdf`,
        volume: 3,
        issue: 2,
        displayTitle: 'Series 9000 Valve - Hyperbaric Chambers: Controlling Gas Delivery'
      },
      {
        itemId: 'vol3-issue1',
        filename: 'ProductHighlightsVol3Issue1.pdf',
        title: 'UDS1V2 Electronic Pressure Switch - Plastic Injection Molding: Controlling Hydraulic Ram Pressure',
        downloadUrl: `${baseUrl}/api/v1/resources/download/56/ProductHighlightsVol3Issue1.pdf`,
        volume: 3,
        issue: 1,
        displayTitle: 'UDS1V2 Electronic Pressure Switch - Plastic Injection Molding: Controlling Hydraulic Ram Pressure'
      }
    ];

    // Add missing Volume 2 issues (not in API response)
    const missingVolume2Data = [
      {
        itemId: 'vol2-issue10',
        filename: 'ProductHighlightsVol2Issue10.pdf',
        title: '9692X Xproof Pressure Switch - Control Panels in Oil & Gas: Controlling Accumulator Pressure',
        downloadUrl: `${baseUrl}/api/v1/resources/download/56/ProductHighlightsVol2Issue10.pdf`,
        volume: 2,
        issue: 10,
        displayTitle: '9692X Xproof Pressure Switch - Control Panels in Oil & Gas: Controlling Accumulator Pressure'
      },
      {
        itemId: 'vol2-issue9',
        filename: 'ProductHighlightsVol2Issue9.pdf',
        title: 'E1H Pressure & Vacuum Switches - Positive Displacement Pumps: Monitor and Control Vacuum/Pressure',
        downloadUrl: `${baseUrl}/api/v1/resources/download/56/ProductHighlightsVol2Issue9.pdf`,
        volume: 2,
        issue: 9,
        displayTitle: 'E1H Pressure & Vacuum Switches - Positive Displacement Pumps: Monitor and Control Vacuum/Pressure'
      },
      {
        itemId: 'vol2-issue8',
        filename: 'ProductHighlightsVol2Issue8.pdf',
        title: 'Series 8000 Pressure Switch - Wind Turbines: Braking Application',
        downloadUrl: `${baseUrl}/api/v1/resources/download/56/ProductHighlightsVol2Issue8.pdf`,
        volume: 2,
        issue: 8,
        displayTitle: 'Series 8000 Pressure Switch - Wind Turbines: Braking Application'
      },
      {
        itemId: 'vol2-issue7',
        filename: 'ProductHighlightsVol2Issue7.pdf',
        title: 'SW2000 Electronic Pressure Switch - Polyurethane Processing Equipment: Foam Auto Seats',
        downloadUrl: `${baseUrl}/api/v1/resources/download/56/ProductHighlightsVol2Issue7.pdf`,
        volume: 2,
        issue: 7,
        displayTitle: 'SW2000 Electronic Pressure Switch - Polyurethane Processing Equipment: Foam Auto Seats'
      },
      {
        itemId: 'vol2-issue6',
        filename: 'ProductHighlightsVol2Issue6.pdf',
        title: 'C9612 Pressure Switch - Compactors & Balers: Monitor and Control Pressure',
        downloadUrl: `${baseUrl}/api/v1/resources/download/56/ProductHighlightsVol2Issue6.pdf`,
        volume: 2,
        issue: 6,
        displayTitle: 'C9612 Pressure Switch - Compactors & Balers: Monitor and Control Pressure'
      }
    ];

    // Add missing data to volumes only if they don't already exist
    missingVolume3Data.forEach(item => {
      if (!fileExists(item.filename, [volume1, volume2, volume3])) {
        volume3.push(item);
      }
    });

    missingVolume2Data.forEach(item => {
      if (!fileExists(item.filename, [volume1, volume2, volume3])) {
        volume2.push(item);
      }
    });

    // Re-sort after adding missing data
    volume1.sort((a, b) => b.issue - a.issue);
    volume2.sort((a, b) => b.issue - a.issue);
    volume3.sort((a, b) => b.issue - a.issue);

    return { volume1, volume2, volume3 };
  }, [resources]);

  const renderVolume = (volumeNumber: number, issues: any[]) => (
    <Box key={volumeNumber} sx={{ mb: 4 }}>
      <Typography 
        variant="h5" 
        sx={{ 
          fontWeight: 'bold', 
          color: theme.palette.primary.main,
          mb: 2,
          borderBottom: `2px solid ${theme.palette.primary.main}`,
          pb: 1
        }}
      >
        Volume {volumeNumber}
      </Typography>
      <Box sx={{ pl: 2 }}>
        {issues.length > 0 ? (
          issues.map((issue) => (
            <Box key={issue.itemId} sx={{ mb: 1.5 }}>
              <Typography 
                variant="body1" 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'flex-start',
                  gap: 1
                }}
              >
                <Typography 
                  component="span" 
                  sx={{ 
                    fontWeight: 'bold',
                    minWidth: '80px',
                    color: theme.palette.text.secondary
                  }}
                >
                  Issue {issue.issue}:
                </Typography>
                {issue.downloadUrl ? (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      cursor: 'pointer',
                      '&:hover': {
                        textDecoration: 'underline',
                        color: '#1565c0',
                      },
                    }}
                                         onClick={() => {
                       if (issue.filename) {
                         handleFileClick(issue.downloadUrl, issue.filename);
                       }
                     }}
                   >
                     <Typography
                      component="span"
                      sx={{
                        textDecoration: 'none',
                        color: '#1976d2',
                      }}
                    >
                      {issue.displayTitle}
                    </Typography>
                  </Box>
                ) : (
                  <Typography component="span">
                    {issue.displayTitle}
                  </Typography>
                )}
              </Typography>
            </Box>
          ))
        ) : (
          <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
            No issues available for Volume {volumeNumber}
          </Typography>
        )}
      </Box>
    </Box>
  );

  return (
    <DashboardLayout 
      title={"Product Highlights"} 
      initialBreadcrumbs={"Sales Tools"} 
      levelBreadcrumbs={""} 
      breadcrumbs={'Sales Tools'} 
      activeMenu={'resources'}
    >
      <Box sx={{ maxWidth: '1200px', mx: 'auto' }}>
        {/* Header */}
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 'bold', 
            mb: 3,
            color: theme.palette.text.primary
          }}
        >
          Product Highlights - Application Information
        </Typography>

        {/* Introduction */}
        <Paper sx={{ p: 3, mb: 4, backgroundColor: '#f8f9fa' }}>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
            Review the Product Highlights below to find information on applications and Barksdale products. 
            Each one-page sheet highlights a specific application, how it works and what our product does. 
            In addition, other potential products used in the application are listed. If you have ideas for 
            applications you would like covered, send an email to{' '}
            <Link 
              href="mailto:nlightfeldt@barksdale.com"
              sx={{ color: '#1976d2', textDecoration: 'none' }}
            >
              Neal Lightfeldt at nlightfeldt@barksdale.com
            </Link>
          </Typography>
          
          <Typography 
            variant="body2" 
            sx={{ 
              color: '#d32f2f', 
              fontWeight: 'bold',
              backgroundColor: '#ffebee',
              p: 2,
              borderRadius: 1,
              border: '1px solid #ffcdd2'
            }}
          >
            Note: Product Highlights are confidential documents intended for Channel Partner review only - 
            they are not intended for review by customers
          </Typography>
        </Paper>

        {/* Error Display */}
        {error && (
          <Paper sx={{ p: 3, mb: 4, backgroundColor: '#ffebee', border: '1px solid #ffcdd2' }}>
            <Typography variant="body1" sx={{ color: '#d32f2f' }}>
              Error loading Product Highlights: {JSON.stringify(error)}
            </Typography>
          </Paper>
        )}

        {/* Loading State */}
        {isLoading ? (
          <Box>
            <Skeleton width="60%" height={40} sx={{ mb: 2 }} />
            <Skeleton width="80%" height={20} sx={{ mb: 1 }} />
            <Skeleton width="70%" height={20} sx={{ mb: 1 }} />
            <Skeleton width="90%" height={20} sx={{ mb: 3 }} />
            <Skeleton width="60%" height={40} sx={{ mb: 2 }} />
            <Skeleton width="80%" height={20} sx={{ mb: 1 }} />
            <Skeleton width="70%" height={20} sx={{ mb: 1 }} />
            <Skeleton width="90%" height={20} sx={{ mb: 3 }} />
            <Skeleton width="60%" height={40} sx={{ mb: 2 }} />
            <Skeleton width="80%" height={20} sx={{ mb: 1 }} />
            <Skeleton width="70%" height={20} sx={{ mb: 1 }} />
            <Skeleton width="90%" height={20} />
          </Box>
        ) : (
          /* Volumes */
          <Box>
            {renderVolume(3, organizedResources.volume3)}
            {organizedResources.volume3.length > 0 && <Divider sx={{ my: 3 }} />}
            {renderVolume(2, organizedResources.volume2)}
            {organizedResources.volume2.length > 0 && <Divider sx={{ my: 3 }} />}
            {renderVolume(1, organizedResources.volume1)}
          </Box>
        )}

        {/* Back to Sales Tools button - at the bottom like main Sales Tools page */}
        <Box sx={{ textAlign: 'left', mt: 4 }}>
          <Button
            variant="contained"
            onClick={() => navigate('/resources')}
            sx={{
              bgcolor: '#c8102e',
              textTransform: 'none',
              fontWeight: 600,
              ...theme.typography.button,
              '&:hover': { bgcolor: '#a30f24' },
            }}
          >
            BACK
          </Button>
        </Box>
      </Box>
    </DashboardLayout>
  )
}

export default ProductHighlights 